import React from "react";
import { MAJOR_MULTIPLIER } from "../utils/pointsUtils";
import { Trophy, Edit2, Save, X } from "lucide-react";

export const labelMapping = {
  place1: "1st Place",
  place2: "2nd Place",
  place3: "3rd Place",
  place4: "4th Place",
  place5: "5th Place",
  place6: "6th Place",
  place7: "7th Place",
  place8: "8th Place",
  place9: "9th Place",
  place10: "10th Place",
  place11: "11th Place",
  place12: "12th Place",
  ctp: "CTP",
  skin: "Skins",
  ctp_skin_cap: "Extras Cap",
  participation: "Participation",
};

/**
 * PointsAllocation - GCHL Brand styled points configuration panel
 *
 * Uses forest green accents, proper dark mode support,
 * and matches the overall admin dashboard styling.
 */
const PointsAllocation = ({
  pointsConfig,
  isEditingPoints,
  toggleEditPoints,
  handlePointsChange,
  savePointsConfig,
  isMajor,
}) => {
  const adjustedPointsConfig = isMajor
    ? Object.fromEntries(
        Object.entries(pointsConfig).map(([key, value]) => [
          key,
          Math.ceil(value * MAJOR_MULTIPLIER),
        ])
      )
    : pointsConfig;

  return (
    <div className="w-full h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-gold-500" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {isMajor ? "Major Event (1.25x)" : "Standard Allocation"}
          </h3>
        </div>
        <button
          onClick={toggleEditPoints}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium
            transition-colors
            ${isEditingPoints
              ? 'bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-navy-600'
              : 'bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400 hover:bg-forest-200 dark:hover:bg-forest-900/60'
            }
          `}
        >
          {isEditingPoints ? (
            <>
              <X className="w-4 h-4" />
              Cancel
            </>
          ) : (
            <>
              <Edit2 className="w-4 h-4" />
              Edit
            </>
          )}
        </button>
      </div>

      {/* Major Event Badge */}
      {isMajor && (
        <div className="mb-4 px-3 py-2 bg-gold-400/20 dark:bg-gold-400/10 border border-gold-400/30 rounded-lg">
          <p className="text-sm text-gold-700 dark:text-gold-400 font-medium">
            Points shown include 1.25x major event multiplier
          </p>
        </div>
      )}

      {/* Points Table */}
      <div className="rounded-xl border border-gray-200 dark:border-navy-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-forest-900 dark:bg-navy-800 text-white">
              <th className="text-left p-3 font-semibold">Category</th>
              <th className="text-right p-3 font-semibold">Points</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
            {Object.entries(adjustedPointsConfig).map(([key, value], index) => (
              <tr
                key={key}
                className={`
                  ${index % 2 === 0
                    ? 'bg-white dark:bg-navy-900'
                    : 'bg-gray-50 dark:bg-navy-800/50'
                  }
                  ${key.startsWith('place') && parseInt(key.replace('place', '')) <= 3
                    ? 'bg-gold-50 dark:bg-gold-900/10'
                    : ''
                  }
                `}
              >
                <td className="p-3 text-gray-700 dark:text-gray-300">
                  <span className={`
                    ${key.startsWith('place') && parseInt(key.replace('place', '')) <= 3
                      ? 'font-semibold text-gold-700 dark:text-gold-400'
                      : ''
                    }
                  `}>
                    {labelMapping[key] || key}
                  </span>
                </td>
                <td className="p-3 text-right">
                  {isEditingPoints ? (
                    <input
                      type="number"
                      value={pointsConfig[key]}
                      onChange={(e) =>
                        handlePointsChange(key, Number(e.target.value))
                      }
                      className="
                        border border-gray-300 dark:border-navy-600
                        rounded-lg px-2 py-1 w-20 text-right text-sm
                        bg-white dark:bg-navy-800
                        text-gray-900 dark:text-gray-100
                        focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                        tabular-nums
                      "
                    />
                  ) : (
                    <span className={`
                      font-semibold tabular-nums
                      ${key.startsWith('place') && parseInt(key.replace('place', '')) <= 3
                        ? 'text-gold-600 dark:text-gold-400'
                        : 'text-gray-900 dark:text-gray-100'
                      }
                    `}>
                      {value}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      {isEditingPoints && (
        <button
          onClick={savePointsConfig}
          className="
            mt-4 w-full
            flex items-center justify-center gap-2
            bg-gold-400 hover:bg-gold-300
            text-navy-900 font-semibold
            px-4 py-3 rounded-xl
            transition-colors
          "
        >
          <Save className="w-5 h-5" />
          Save Points Configuration
        </button>
      )}
    </div>
  );
};

export default PointsAllocation;
